// Enhanced secure storage with encryption for authentication tokens
// Provides maximum client-side security without backend changes
import Logger from "./logger";

interface CookieOptions {
  expires?: Date;
  maxAge?: number;
  domain?: string;
  path?: string;
  secure?: boolean;
  sameSite?: "strict" | "lax" | "none";
}

// Simple encryption utility (client-side only)
class SimpleEncryption {
  private static readonly SECRET_KEY =
    "MARS_2025_SECURE_KEY_" + window.location.hostname;

  static encrypt(text: string): string {
    try {
      // Simple XOR encryption (better than plain text)
      let encrypted = "";
      for (let i = 0; i < text.length; i++) {
        const charCode =
          text.charCodeAt(i) ^
          this.SECRET_KEY.charCodeAt(i % this.SECRET_KEY.length);
        encrypted += String.fromCharCode(charCode);
      }
      return btoa(encrypted); // Base64 encode
    } catch (error) {
      Logger.error("Encryption failed:", error);
      return text; // Fallback to plain text
    }
  }

  static decrypt(encryptedText: string): string {
    try {
      const decoded = atob(encryptedText); // Base64 decode
      let decrypted = "";
      for (let i = 0; i < decoded.length; i++) {
        const charCode =
          decoded.charCodeAt(i) ^
          this.SECRET_KEY.charCodeAt(i % this.SECRET_KEY.length);
        decrypted += String.fromCharCode(charCode);
      }
      return decrypted;
    } catch (error) {
      Logger.error("Decryption failed:", error);
      return encryptedText; // Return as-is if decryption fails
    }
  }
}

class SecureStorage {
  private static readonly TOKEN_KEY = "auth_token";
  private static readonly REFRESH_KEY = "refresh_token";
  private static readonly USER_DATA_KEY = "user_data";

  // In-memory storage for maximum security (cleared on page refresh)
  private static memoryStore: Map<string, string> = new Map();
  private static useMemoryStorage = true;

  // Enhanced cookie setting with encryption
  private static setCookie(
    name: string,
    value: string,
    options: CookieOptions = {}
  ) {
    const defaults: CookieOptions = {
      path: "/",
      secure: window.location.protocol === "https:",
      sameSite: "strict",
      maxAge: 24 * 60 * 60, // 24 hours
    };

    const opts = { ...defaults, ...options };

    // Encrypt the value before storing
    const encryptedValue = SimpleEncryption.encrypt(value);

    let cookieString = `${name}=${encodeURIComponent(encryptedValue)}`;

    if (opts.expires) {
      cookieString += `; expires=${opts.expires.toUTCString()}`;
    }

    if (opts.maxAge) {
      cookieString += `; max-age=${opts.maxAge}`;
    }

    if (opts.domain) {
      cookieString += `; domain=${opts.domain}`;
    }

    if (opts.path) {
      cookieString += `; path=${opts.path}`;
    }

    if (opts.secure) {
      cookieString += `; secure`;
    }

    if (opts.sameSite) {
      cookieString += `; samesite=${opts.sameSite}`;
    }

    document.cookie = cookieString;
  }

  // Get and decrypt cookie value
  private static getCookie(name: string): string | null {
    const nameEQ = name + "=";
    const ca = document.cookie.split(";");

    for (let i = 0; i < ca.length; i++) {
      let c = ca[i];
      while (c.charAt(0) === " ") c = c.substring(1, c.length);
      if (c.indexOf(nameEQ) === 0) {
        const encryptedValue = decodeURIComponent(
          c.substring(nameEQ.length, c.length)
        );
        return SimpleEncryption.decrypt(encryptedValue);
      }
    }
    return null;
  }

  // Remove cookie
  private static removeCookie(name: string, path: string = "/") {
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; secure; samesite=strict`;
  }

  // Store in memory first, fallback to encrypted cookie
  private static setSecureValue(
    key: string,
    value: string,
    cookieOptions?: CookieOptions
  ) {
    if (this.useMemoryStorage) {
      this.memoryStore.set(key, value);
    }

    // Always store encrypted backup in cookie
    this.setCookie(key, value, {
      secure: true,
      sameSite: "strict",
      ...cookieOptions,
    });
  }

  // Get from memory first, fallback to encrypted cookie
  private static getSecureValue(key: string): string | null {
    // Try memory first
    if (this.useMemoryStorage && this.memoryStore.has(key)) {
      return this.memoryStore.get(key) || null;
    }

    // Fallback to encrypted cookie
    return this.getCookie(key);
  }

  // Remove from both memory and cookie
  private static removeSecureValue(key: string) {
    this.memoryStore.delete(key);
    this.removeCookie(key);
  }

  // Public methods for token management
  static setToken(token: string) {
    this.setSecureValue(this.TOKEN_KEY, token, {
      maxAge: 24 * 60 * 60, // 24 hours
    });
  }

  static getToken(): string | null {
    return this.getSecureValue(this.TOKEN_KEY);
  }

  static setRefreshToken(refreshToken: string) {
    this.setSecureValue(this.REFRESH_KEY, refreshToken, {
      maxAge: 7 * 24 * 60 * 60, // 7 days
    });
  }

  static getRefreshToken(): string | null {
    return this.getSecureValue(this.REFRESH_KEY);
  }

  static setUserData(userData: any) {
    // Store non-sensitive user data in localStorage
    try {
      localStorage.setItem(this.USER_DATA_KEY, JSON.stringify(userData));
    } catch (error) {
      Logger.error("Failed to store user data:", error);
    }
  }

  static getUserData(): any | null {
    try {
      const data = localStorage.getItem(this.USER_DATA_KEY);
      return data ? JSON.parse(data) : null;
    } catch (error) {
      Logger.error("Failed to retrieve user data:", error);
      return null;
    }
  }

  static clearAll() {
    // Clear memory storage
    this.memoryStore.clear();

    // Remove cookies
    this.removeSecureValue(this.TOKEN_KEY);
    this.removeSecureValue(this.REFRESH_KEY);

    // Remove user data
    localStorage.removeItem(this.USER_DATA_KEY);
  }

  // Check if tokens exist
  static hasValidSession(): boolean {
    return !!(this.getToken() && this.getRefreshToken());
  }

  // Security status check
  static getSecurityInfo() {
    return {
      memoryStorage: this.useMemoryStorage,
      hasTokenInMemory: this.memoryStore.has(this.TOKEN_KEY),
      hasTokenInCookie: !!this.getCookie(this.TOKEN_KEY),
      isSecureContext: window.location.protocol === "https:",
      encryption: "XOR + Base64",
    };
  }

  // Force memory-only mode (most secure)
  static enableMemoryOnlyMode() {
    this.useMemoryStorage = true;
    // Move existing cookie tokens to memory
    const token = this.getCookie(this.TOKEN_KEY);
    const refresh = this.getCookie(this.REFRESH_KEY);

    if (token) {
      this.memoryStore.set(this.TOKEN_KEY, token);
      this.removeCookie(this.TOKEN_KEY);
    }

    if (refresh) {
      this.memoryStore.set(this.REFRESH_KEY, refresh);
      this.removeCookie(this.REFRESH_KEY);
    }
  }
}

export default SecureStorage;
